import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DocumentPermission, Group, useSharingApi } from '@/hooks/use-shareing-api';
import { getUserInfoFromLocal } from '@/lib/utils';

import { S3File } from '../Home';

interface DialogShareProps {
  open: boolean;
  onClose: () => void;
  file: S3File | null;
  apiBaseUrl: string;
  rootUrlHostApi: string;
}
export interface IBodyDocumentPermission {
  id?: string;
  path: string;
  isFolder?: boolean;
  isPublic?: boolean;
  permissions: DocumentPermission;
  userId?: string;
  groupId?: string;
  groupname?: string;
  grandtedBy: string;
  username?: string;
  email?: string;
  users?: number;
}

const permissionOptions = [
  // { value: 'VIEW_DOCUMENT', label: 'Chỉ xem' },
  { value: 'EDIT_DOCUMENT', label: 'Chỉnh sửa' },
  // { value: 'DOWNLOAD_DOCUMENT', label: 'Tải xuống' },
];

export default function DialogShare({
  onClose,
  open,
  file,
  apiBaseUrl,
  rootUrlHostApi,
}: DialogShareProps) {
  const [mode, setMode] = useState('users');
  const [searchUser, setSearchUser] = useState('');
  const [searchGroup, setSearchGroup] = useState('');
  const [searchSharedUser, setSearchSharedUser] = useState('');
  const [searchSharedGroup, setSearchSharedGroup] = useState('');
  const [permission, setPermission] = useState<DocumentPermission>(
    DocumentPermission.VIEW_DOCUMENT,
  );
  const [docPermissions, setDocPermissions] = useState<IBodyDocumentPermission[]>([]);

  const {
    users,
    groups,
    sharedUsers,
    sharedGroups,
    fetchData,
    loading,
    postGranted,
    loadingGrandted,
    setSharedGroups,
    setSharedUsers,
  } = useSharingApi(apiBaseUrl, rootUrlHostApi);

  const currentUser = getUserInfoFromLocal();

  const filePath = file ? file.key : '';

  const filteredUsers = users.filter(
    (u) =>
      currentUser?.id !== u.id &&
      (u.username.toLowerCase().includes(searchUser.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchUser.toLowerCase())),
  );
  const filteredGroups = groups.filter((g) =>
    g.groupname.toLowerCase().includes(searchGroup.toLowerCase()),
  );
  const filteredSharedUsers = sharedUsers.filter(
    (u) =>
      u.username?.toLowerCase().includes(searchSharedUser.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchSharedUser.toLowerCase()),
  );
  const filteredSharedGroups = sharedGroups.filter((g) =>
    g.groupname?.toLowerCase().includes(searchSharedGroup.toLowerCase()),
  );

  // const handleChangePermission = (v: DocumentPermission) => {
  //   setPermission(v);

  //   setDocPermissions((prev) => prev.map((data) => ({ ...data, permissions: v })));
  // };

  const handleClearSelection = () => {
    if (mode === 'users') {
      setSearchUser('');
      setSearchSharedUser('');
    } else {
      setSearchGroup('');
      setSearchSharedGroup('');

      setMode('users');
    }

    setDocPermissions([]);
    setPermission(DocumentPermission.EDIT_DOCUMENT);
  };

  useEffect(() => {
    if (open) {
      fetchData(filePath);
    } else {
      handleClearSelection();
    }
  }, [open, filePath]);

  const handleChangeTab = (value: 'users' | 'groups') => {
    if (value !== mode) {
      if (mode === 'users') {
        setSearchUser('');
        setSearchSharedUser('');
      } else {
        setSearchGroup('');
        setSearchSharedGroup('');
      }
    }
    setMode(value);
  };

  const handleToggleUser = (userId: string) => {
    if (!file) return;

    setDocPermissions((prev) => {
      const existed = prev.find((docPermission) => docPermission.userId === userId);

      if (existed) {
        return prev.filter((docPermission) => docPermission.userId !== userId);
      }

      const filePath = file.key + file.name;
      const newDocPermission: IBodyDocumentPermission = {
        path: filePath,
        permissions: permission,
        userId,
        grandtedBy: currentUser?.id,
      };

      return [...prev, newDocPermission];
    });
  };

  const handleToggleGroup = (group: Group) => {
    if (!file) return;

    const groupname = group.groupname;

    setDocPermissions((prev) => {
      const existed = prev.find((docPermission) => docPermission.groupname === groupname);

      if (existed) {
        return prev.filter((docPermission) => docPermission.groupname !== groupname);
      }

      const filePath = file.key + file.name;
      const newDocPermission: IBodyDocumentPermission = {
        path: filePath,
        permissions: permission,
        groupname,
        groupId: group.id,
        grandtedBy: currentUser?.id,
        users: group.users,
      };

      return [...prev, newDocPermission];
    });
  };

  const handleChangeShared = async (
    shared: IBodyDocumentPermission,
    newPermission: DocumentPermission,
    type: 'users' | 'groups',
  ) => {
    const newShared = { ...shared, permissions: newPermission };

    setDocPermissions((prev) => {
      const updated = prev.map((item) => (item.id === newShared.id ? newShared : item));

      const exists = prev.some((item) => item.id === newShared.id);
      if (!exists) {
        updated.push(newShared);
      }

      return updated;
    });

    if (type === 'users') {
      setSharedUsers((prev) => prev.map((user) => (user.id === shared.id ? newShared : user)));
    } else {
      setSharedGroups((prev) => prev.map((group) => (group.id === shared.id ? newShared : group)));
    }
  };

  const handleShare = async () => {
    if (file) {
      const filePath = file?.key || '' + file?.name;
      const resposne = await postGranted(filePath, currentUser?.id, docPermissions);

      if (resposne?.status == 200) {
        setDocPermissions([]);
      }
    }
  };

  const listContainerStyle =
    'flex-1 min-h-0 overflow-y-auto border rounded-lg px-3 py-2 bg-background shadow-sm';

  if (!file) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Chia sẻ tài liệu: {file.name}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="users" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-2 w-1/2 mx-auto mb-4 bg-muted rounded-lg">
            <TabsTrigger value="users" className="py-2" onClick={() => handleChangeTab('users')}>
              Người dùng
            </TabsTrigger>
            <TabsTrigger value="groups" className="py-2" onClick={() => handleChangeTab('groups')}>
              Nhóm
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="flex-1 h-full min-h-0 overflow-hidden">
            <div className="grid grid-cols-2 gap-6 h-full">
              <div className="flex flex-col flex-1 min-h-0">
                <p className="text-sm font-semibold mb-2">Chia sẻ với người dùng:</p>
                <Input
                  placeholder="Tìm người dùng..."
                  className="mb-3 border-input bg-background shadow-sm"
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                />
                <div className={listContainerStyle}>
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <p className="text-muted-foreground italic text-center h-full flex items-center justify-center">
                      Không tìm thấy người dùng.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {filteredUsers.map((user) => (
                        <li
                          key={user.id}
                          onClick={() => handleToggleUser(user.id)}
                          className="flex flex-col space-y-1 cursor-pointer hover:bg-accent p-2 rounded-md transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <Checkbox
                              id={`user-${user.id}`}
                              checked={!!docPermissions.find((dp) => dp.userId === user.id)}
                            />
                            <Label className="flex-1">
                              <p className="font-medium">{user.username}</p>
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                              <p className="text-xs text-muted-foreground">{user.groupname}</p>
                            </Label>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="flex flex-col flex-1 min-h-0">
                <p className="text-sm font-semibold mb-2">Đã chia sẻ với người dùng:</p>
                <Input
                  placeholder="Tìm người dùng đã chia sẻ..."
                  className="mb-3 border-input bg-background shadow-sm"
                  value={searchSharedUser}
                  onChange={(e) => setSearchSharedUser(e.target.value)}
                />
                <div className={listContainerStyle}>
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredSharedUsers.length === 0 ? (
                    <p className="text-muted-foreground italic text-center h-full flex items-center justify-center">
                      Chưa chia sẻ với người dùng nào.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {filteredSharedUsers.map((user) => (
                        <li
                          key={user.id}
                          className="flex items-center justify-between gap-4 hover:bg-accent p-2 rounded-md transition-colors"
                        >
                          <div className="flex flex-col">
                            <p className="font-medium">{user.username}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                            <p className="text-xs text-muted-foreground">{user.groupname}</p>
                          </div>

                          <Select
                            value={user.permissions}
                            onValueChange={(v: DocumentPermission) =>
                              handleChangeShared(user, v, 'users')
                            }
                            disabled={user.permissions === DocumentPermission.OWNER_DOCUMENT}
                          >
                            <SelectTrigger className="w-[120px]">
                              <SelectValue placeholder="Chọn quyền" />
                            </SelectTrigger>
                            <SelectContent>
                              {permissionOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                              {user.permissions === DocumentPermission.OWNER_DOCUMENT && (
                                <SelectItem
                                  key={DocumentPermission.OWNER_DOCUMENT}
                                  value={DocumentPermission.OWNER_DOCUMENT}
                                >
                                  Chủ sở hữu
                                </SelectItem>
                              )}
                              <SelectItem key={'None'} value={'None'}>
                                Thu hồi
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="groups" className="flex-1 h-full min-h-0 overflow-hidden">
            <div className="grid grid-cols-2 gap-6 h-full">
              <div className="flex flex-col flex-1 min-h-0">
                <p className="text-sm font-semibold mb-2">Chia sẻ với nhóm:</p>
                <Input
                  placeholder="Tìm nhóm..."
                  className="mb-3 border-input bg-background shadow-sm"
                  value={searchGroup}
                  onChange={(e) => setSearchGroup(e.target.value)}
                />
                <div className={listContainerStyle}>
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredGroups.length === 0 ? (
                    <p className="text-muted-foreground italic text-center h-full flex items-center justify-center">
                      Không tìm thấy nhóm.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {filteredGroups.map((group) => (
                        <li
                          key={group.groupname}
                          onClick={() => handleToggleGroup(group)}
                          className="flex items-center space-x-3 cursor-pointer hover:bg-accent p-2 rounded-md transition-colors"
                        >
                          <Checkbox
                            id={`group-${group.groupname}`}
                            checked={
                              !!docPermissions.find((dp) => dp.groupname === group.groupname)
                            }
                          />
                          <Label className="font-medium">
                            <p className="font-medium">{group.groupname}</p>
                            <p className="text-xs text-muted-foreground">
                              Số lượng: {group.users || 0}
                            </p>
                          </Label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="flex flex-col flex-1 min-h-0">
                <p className="text-sm font-semibold mb-2">Đã chia sẻ với nhóm:</p>
                <Input
                  placeholder="Tìm nhóm đã chia sẻ..."
                  className="mb-3 border-input bg-background shadow-sm"
                  value={searchSharedGroup}
                  onChange={(e) => setSearchSharedGroup(e.target.value)}
                />
                <div className={listContainerStyle}>
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredSharedGroups.length === 0 ? (
                    <p className="text-muted-foreground italic text-center h-full flex items-center justify-center">
                      Chưa chia sẻ với nhóm nào.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {filteredSharedGroups.map((group) => (
                        <li
                          key={group.groupname}
                          className="flex items-center justify-between gap-4 hover:bg-accent p-2 rounded-md transition-colors"
                        >
                          <div className="flex flex-col">
                            <p className="font-medium">{group.groupname}</p>
                            <p className="text-xs text-muted-foreground">
                              Số lượng: {group.users || 0}
                            </p>
                          </div>

                          <Select
                            value={group.permissions}
                            onValueChange={(v: DocumentPermission) =>
                              handleChangeShared(group, v, 'groups')
                            }
                          >
                            <SelectTrigger className="w-[120px]">
                              <SelectValue placeholder="Chọn quyền" />
                            </SelectTrigger>
                            <SelectContent>
                              {permissionOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                              <SelectItem key={'None'} value={'None'}>
                                Thu hồi
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* <div className="mt-4 bg-muted/50 p-4 rounded-lg">
          <Label className="font-medium mb-2 block">Phân quyền:</Label>
          <Select value={permission} onValueChange={handleChangePermission}>
            <SelectTrigger className="w-1/2 border border-input bg-background shadow-sm hover:bg-accent">
              <SelectValue placeholder="Chọn quyền truy cập" />
            </SelectTrigger>
            <SelectContent>
              {permissionOptions.map((option) => (
                <SelectItem value={option.value} className="flex items-center gap-2">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div> */}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Huỷ
          </Button>
          <Button
            variant="default"
            onClick={handleShare}
            disabled={docPermissions.length === 0 || loadingGrandted}
            className="w-[120px] justify-center"
          >
            {loadingGrandted ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lưu thay đổi'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
